/**
 * OPINION-1 (2026-09-30): one opinion for every served car, not only for the few picks.
 *
 * Coen: "looking at thousands of cars and there's only dozens of picks". The pipeline already
 * judges every car; the site only showed the ~0.1% that became picks, and before that (until
 * 2026-09-02) a wall of "Not recommended" cards that read as "the tool finds nothing". Both
 * hide the actual finding. Measured on the served table 2026-09-30 (1,051,880 cars):
 *
 *   recommended   794   every check passed (shown while the dealer page was checked < 24 h)
 *   unverified  ~2.3k   passed the checks the model can do; the dealer page could not be read
 *   caution     27.5k   margin clears our uncertainty bar, but a named check objects
 *   thin        49.3k   import beats buying in NL, by less than our valuation can be off
 *   suspect      7.0k   the asking price is implausible for the car (typo, monthly/export price)
 *   no_gain    821.2k   buying the same car in NL is as cheap or cheaper after all costs
 *   no_value   153.6k   no honest Dutch value (no comparables, classic, other generation)
 *
 * The order below is the order of precedence. SIMPLE-1 (2026-10-13): the card shows `recommended`
 * as Koopkans, `unverified` and `downside` (T4C-NET-1) as Controleer zelf, and every other verdict
 * as Geen koopkans + its why.
 * Only `recommended` may use the recommendation's green cues (BUYER-HONESTY-1).
 */
import { valuationStatus } from './valuationStatus.js'
import { isUnverifiedCandidate } from './unverifiedCandidates.js'
import { NET_LOSS_LIMIT_EUR } from './picks.js'

export const VERDICTS = Object.freeze({
  recommended: { label: 'Recommended', tone: 'positive',
    why: 'Clears every check: the margin exceeds our valuation uncertainty and the dealer page confirmed the car in the last 24 hours. Still check it before you buy.' },
  downside: { label: 'Promising — check the risk yourself', tone: 'caution',
    why: 'Passed every check, but if the car only sells at the low end of our Dutch value range you lose {loss} after all costs. Only worth it if you accept that risk.' },
  unverified: { label: 'Promising — check the advert yourself', tone: 'caution',
    why: 'Our checks found room, but the dealer page could not be read automatically. Open the advert and check price, condition, location and taxes yourself.' },
  caution: { label: 'Promising, but one check objects', tone: 'caution',
    why: 'The margin clears our valuation uncertainty, but one of our checks objects. The reason is shown with the car.' },
  thin: { label: 'Margin too thin for our uncertainty', tone: 'neutral',
    why: 'Importing beats buying in the Netherlands, but by less than our Dutch value can be off. Only worth it if you can value this car more precisely yourself.' },
  suspect_price: { label: 'Price looks wrong', tone: 'muted',
    why: 'The asking price is implausible for this car: often a typo, a monthly or export price, or a damaged car. We do not value it as a purchase price.' },
  no_gain: { label: 'Not worth importing', tone: 'muted',
    why: 'The same car costs the same or less in the Netherlands once BPM, transport, fees, the sale spread and a condition reserve are counted.' },
  gone: { label: 'No longer for sale', tone: 'muted',
    why: 'The seller has removed or sold this car.' },
  no_value: { label: 'No opinion possible', tone: 'muted', why: null },
})

const num = value => (value === null || value === undefined || value === '' ? NaN : Number(value))
const euros = value => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)

/**
 * @param {object} car  a card object (App.jsx transform) or a raw served row
 * @param {{recommendationCurrent?: boolean}} opts  whether the pick's evidence is current
 * @returns {{key: string, label: string, tone: string, why: string|null}}
 */
export function verdictOf(car, { recommendationCurrent = false } = {}) {
  const pick = (key, why, vars) => ({ key, ...VERDICTS[key], why: why ?? VERDICTS[key].why, vars })
  if (!car) return pick('no_value', null)
  const value = num(car.estimated_sales_price_nl ?? car.pred_target_price)
  if (!(value > 0)) return pick('no_value', valuationStatus(car).why)
  if (String(car.source_flag || '').toLowerCase() === 'gone') return pick('gone')
  if (num(car.buy_score) > 0) {
    if (!recommendationCurrent) {
      return pick('unverified', 'Passed every check at its last dealer check, but that check is older than 24 hours. Check the advert yourself.')
    }
    // T4C-NET-1: the stored figure the Koopkans list filters on, so label and section agree.
    const safe = num(car.profit_safe_eur)
    return safe < -NET_LOSS_LIMIT_EUR ? pick('downside', undefined, { loss: euros(-safe) }) : pick('recommended')
  }
  const revenue = num(car.expected_revenue)
  const net = num(car.expected_margin_net_pct)
  if (!(revenue > 0) || (Number.isFinite(net) && net <= 0)) return pick('no_gain')
  const anomaly = car.price_flag_anomaly
  if (anomaly === true || anomaly === 'true' || anomaly === 1) return pick('suspect_price')
  const reason = String(car.buy_reason || '')
  if (isUnverifiedCandidate(car)) return pick('unverified', reason.startsWith('source verification: cash price') ? reason : undefined)
  const required = num(car.min_margin_required)
  if (Number.isFinite(net) && required > 0 && net < required) return pick('thin')
  return pick('caution', reason || null)
}
